import { queryContract } from "./Contract";
import { updateNFTStore, bulkUpsertNFTs } from "../../ReduxSlices/NFTSlice";
import axios from "axios";
import { api, apiGet, setAuthToken } from "./Api";
import { toBech32, fromHex } from "@cosmjs/encoding";
import { createThirdwebClient } from "thirdweb";
import { defineChain } from "thirdweb/chains";
import { inAppWallet, createWallet } from "thirdweb/wallets";
import { nibiruApi } from './NibiruApi';

export const thiredWebClient = createThirdwebClient({
  clientId:
    process.env.REACT_APP_THIRDWEB_CLIENT_ID ||
    "",
});

// Nibiru EVM mainnet chain (0x1AF4 = 6900)
export const nibiruChain = defineChain(6900);

// Thirdweb wallets config (social login via inAppWallet + Coinbase extension)
export const thirdwebWallets = [
  inAppWallet({
    auth: {
      options: ["email", "google", "apple", "facebook", "coinbase", "passkey"],
    },
  }),
  createWallet("com.coinbase.wallet"),
];

export const truncateWalletAddress = (address, forward = 5, backward = 4) => {
  if (address)
    return (
      address.toString().substring(0, forward) +
      "..." +
      address.toString().substring(address.toString().length - backward)
    );
};

/** Lowercase trim; matches backend normalize for comparisons. */
export const normalizeWalletAddress = (account) => {
  if (!account) return null;
  return String(account).trim().toLowerCase();
};

/**
 * True if two addresses are the same wallet (exact match or EVM 0x vs Nibiru nibi1).
 */
export const isSameWalletAddress = (addr1, addr2) => {
  const a = normalizeWalletAddress(addr1);
  const b = normalizeWalletAddress(addr2);
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    if (a.startsWith("0x") && b.startsWith("nibi")) {
      const fromEvm = toBech32("nibi", fromHex(a.slice(2)));
      return fromEvm === b;
    }
    if (b.startsWith("0x") && a.startsWith("nibi")) {
      const fromEvm = toBech32("nibi", fromHex(b.slice(2)));
      return fromEvm === a;
    }
  } catch (_) {}
  return false;
};

/** Resolve NFT in Redux when token_id key is string vs number mismatch. */
export const resolveNftFromStore = (nfts, nftId) => {
  if (!nfts || nftId === undefined || nftId === null || nftId === "") return null;
  if (nfts[nftId]) return nfts[nftId];
  const idStr = String(nftId);
  const key = Object.keys(nfts).find((k) => String(k) === idStr);
  return key ? nfts[key] : null;
};

/**
 * Inbox path for message notifications: uses chat owner from API when Redux NFT
 * is missing or owner is stored as nibi while the user connected with 0x (or vice versa).
 */
export const resolveMessageInboxPath = async ({
  account,
  chatId,
  nftId,
  nfts,
  pathname = "",
}) => {
  if (!account || !chatId) return "/dashboard";

  try {
    const info = await api("chat/getChattingInfo", {
      id: String(chatId),
      reader: account,
    });
    if (info?.owner != null && String(info.owner).trim() !== "") {
      const isPropertyOwner = isSameWalletAddress(account, info.owner);
      return isPropertyOwner
        ? `/dashboard/host/inbox?chat=${chatId}`
        : `/dashboard/traveler/inbox?chat=${chatId}`;
    }
  } catch (_) {}

  const nft = resolveNftFromStore(nfts, nftId);
  if (nft?.access?.owner) {
    const isPropertyOwner = isSameWalletAddress(account, nft.access.owner);
    return isPropertyOwner
      ? `/dashboard/host/inbox?chat=${chatId}`
      : `/dashboard/traveler/inbox?chat=${chatId}`;
  }

  const preferHost =
    typeof pathname === "string" && pathname.includes("/dashboard/host");
  return preferHost
    ? `/dashboard/host/inbox?chat=${chatId}`
    : `/dashboard/traveler/inbox?chat=${chatId}`;
};

export const navigateToMessageNotificationInbox = async ({
  item,
  account,
  nfts,
  pathname,
  navigate,
}) => {
  const chatId = item?.data?.id;
  if (!chatId || !account || !navigate) return;
  const path = await resolveMessageInboxPath({
    account,
    chatId,
    nftId: item?.data?.nftId,
    nfts,
    pathname,
  });
  navigate(path);
};

/** Deduplicate chat rows that differ only by timestamp string formatting (socket vs HTTP). */
export const isSameChatMessage = (a, b) => {
  if (!a || !b) return false;
  const sameSender =
    isSameWalletAddress(a.sender, b.sender) ||
    String(a.sender || "").trim() === String(b.sender || "").trim();
  if (!sameSender) return false;
  const ta = new Date(a.generatedTime).getTime();
  const tb = new Date(b.generatedTime).getTime();
  if (isNaN(ta) || isNaN(tb)) return false;
  if (Math.abs(ta - tb) > 2000) return false;
  const txta = String(a.text || "");
  const txtb = String(b.text || "");
  if (txta !== txtb) return false;
  const imga = a.image || null;
  const imgb = b.image || null;
  return imga === imgb;
};

export const dedupeChatMessages = (messages) => {
  if (!Array.isArray(messages)) return [];
  const out = [];
  for (const m of messages) {
    if (!out.some((e) => isSameChatMessage(e, m))) out.push(m);
  }
  return out.sort(
    (x, y) =>
      new Date(x.generatedTime).getTime() -
      new Date(y.generatedTime).getTime()
  );
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const retryOperation = async (
  operation,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
) => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay);
    }
    throw error;
  }
};

const toLowerCaseSafe = (value) =>
  typeof value === "string" ? value.toLowerCase() : value;

export const normalizePropertyForStore = (property, extra = {}) => {
  if (!property || !property.token_id) {
    return null;
  }

  // 🚀 FIX: Ensure new array reference for rentals.rentals to trigger Redux updates
  const rentals = property.rentals ? {
    ...property.rentals,
    rentals: property.rentals.rentals ? [...property.rentals.rentals] : []
  } : {};

  return {
    token_id: property.token_id,
    metaData: property.metaData || {},
    short: property.short || {},
    rentals: rentals,
    access: property.access || {},
    sync_status: property.sync_status,
    pendingActions: property.pendingActions,
    metadataVersion: property.metadataVersion,
    metadataHash: property.metadataHash,
    pendingMetadataHash: property.pendingMetadataHash,
    mint: property.mint,
    ...extra,
  };
};

const dispatchPropertyToStore = (dispatch, property, extra = {}) => {
  if (!dispatch) return;
  const payload = normalizePropertyForStore(property, extra);
  if (payload) {
    dispatch(updateNFTStore(payload));
  }
};

// 🚀 OPTIMIZED: Only dispatch if properties actually changed to prevent unnecessary re-renders
const dispatchPropertiesBatch = (dispatch, properties, extra = {}) => {
  if (!dispatch || !Array.isArray(properties) || properties.length === 0) {
    return;
  }
  const payload = properties
    .map((property) => normalizePropertyForStore(property, extra))
    .filter(Boolean);
  if (payload.length) {
    // Use requestIdleCallback or setTimeout to batch updates and prevent blocking
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        dispatch(bulkUpsertNFTs(payload));
      }, { timeout: 100 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        dispatch(bulkUpsertNFTs(payload));
      }, 0);
    }
  }
};

export const updateToken = async (data, endpt, dispatch) => {
  try {
    // Replace direct queryContract calls with rate-limited versions
    const [res, short, rentals] = await Promise.all([
      nibiruApi.queryContractWithLimit(
        data.contract,
        { all_nft_info: { token_id: data.token_id } },
        'high' // High priority for individual token updates
      ),
      nibiruApi.queryContractWithLimit(
        data.contract,
        { nft_info_short_term_rental: { token_id: data.token_id } },
        'high'
      ),
      nibiruApi.queryContractWithLimit(
        data.contract,
        { nft_rentals: { token_id: data.token_id } },
        'high'
      ),
    ]);

    console.log(data.token_id, "token_id");

    const metaData = await retryOperation(() =>
      api("property/getProperty", { token_id: data.token_id })
    );

    console.log(metaData, "metadata");

    // Use raw price value from backend only - backend already has correct format
    // If backend doesn't have price OR backend price looks like raw format, convert contract value
    let pricePerDay = metaData?.short?.price_per_day;
    const contractPrice = short?.price_per_day || 0;
    const usdcDecimals = Number(process.env.REACT_APP_USDC_DECIMALS) || 6;
    
    // Check if backend price is missing or looks like raw format (>= 1000 and whole number)
    const backendPriceIsRaw = pricePerDay !== undefined && pricePerDay !== null && 
                               pricePerDay >= 1000 && Number.isInteger(Number(pricePerDay));
    
    if (pricePerDay === undefined || pricePerDay === null || backendPriceIsRaw) {
      // Convert contract value (raw format) to display format
      // Contract stores as: 500000 = 0.5 USDC, 70000000 = 70 USDC (with 6 decimals)
      if (contractPrice > 0) {
        // Always convert contract values - they're always in raw format
        pricePerDay = contractPrice / (10 ** usdcDecimals);
      } else {
        pricePerDay = backendPriceIsRaw ? (pricePerDay / (10 ** usdcDecimals)) : (pricePerDay || 0);
      }
    }

    if (res?.access && short) {
      // ✅ IMPORTANT: Do NOT send icalSync or manuallyBlockedDates to blockchain
      // These are DB-only fields for UI blocking, not blockchain rentals
      await retryOperation(() =>
        api("property/updateProperty", {
          token_id: data.token_id,
          short: {
            ...short,
            price_per_day: pricePerDay,
          },
          rentals: rentals, // Only blockchain rentals, NOT manual/iCal blocks
          access: res?.access,
        })
      );
    }

    // ✅ Store icalSync and manuallyBlockedDates in Redux for UI blocking only
    // These are NOT sent to blockchain - they're DB-only fields
    dispatch(
      updateNFTStore({
        token_id: data.token_id,
        metaData: metaData?.metaData,
        short: {
          ...short,
          price_per_day: pricePerDay,
        },
        rentals: rentals, // Only blockchain rentals
        access: res?.access,
        icalSync: metaData?.icalSync || null, // ✅ DB-only: For UI calendar blocking
        manuallyBlockedDates: metaData?.manuallyBlockedDates || [], // ✅ DB-only: For UI calendar blocking
        error: null,
        sync_status: "confirmed",
      })
    );
  } catch (error) {
    console.error(`Error updating token ${data.token_id}:`, error);
    dispatch(
      updateNFTStore({
        token_id: data.token_id,
        error: "Failed to load property data",
      })
    );
  }
};

export const getTokensFromBackend = async (account, dispatch) => {
  try {
    if (!account) return [];
    const response = await retryOperation(() =>
      api("property/getProperties", {
        walletAddress: toLowerCaseSafe(account),
      })
    );
    const properties = response?.properties || response || [];
    dispatchPropertiesBatch(dispatch, properties, { isInitialLoad: true });
    return properties;
  } catch (error) {
    console.error("Error fetching properties from backend:", error);
    return [];
  }
};

export const getHostProperties = async ({
  walletAddress,
  view = "all",
  search = "",
  page = 1,
  limit = 40,
  syncStatuses = [],
  dispatch,
}) => {
  try {
    if (!walletAddress) {
      return { success: false, properties: [] };
    }

    const params = {
      wallet: toLowerCaseSafe(walletAddress),
      view,
      search,
      page,
      limit,
    };

    if (Array.isArray(syncStatuses) && syncStatuses.length) {
      params.sync = syncStatuses.join(",");
    }

    const response = await retryOperation(() =>
      apiGet("property/host/properties", params)
    );

    if (response?.success) {
      dispatchPropertiesBatch(dispatch, response.properties || []);
    }

    return response;
  } catch (error) {
    console.error("Error fetching host properties:", error);
    return { success: false, properties: [] };
  }
};

// 🚀 OPTIMIZED: Get verified NFTs from backend (pre-filtered)
export const getMyVerifiedNFTs = async (account, dispatch) => {
  const response = await getHostProperties({ walletAddress: account, dispatch });
  return response?.properties || [];
};

// 🚀 OPTIMIZED: Get my listings from backend (pre-filtered)
export const getMyListings = async (account, dispatch) => {
  const response = await getHostProperties({
    walletAddress: account,
    view: "listed",
    dispatch,
  });
  return response?.properties || [];
};

const mutateListingStatus = async ({
  tokenId,
  walletAddress,
  shouldList,
  dispatch,
  price_per_day,
  minimum_stay,
  cancellation,
  denom,
}) => {
  if (!tokenId || !walletAddress) {
    throw new Error("tokenId and walletAddress are required");
  }
  const route = shouldList
    ? `property/host/properties/${tokenId}/list`
    : `property/host/properties/${tokenId}/unlist`;

  const payload = {
    walletAddress: toLowerCaseSafe(walletAddress),
  };

  // Include listing details if provided
  if (price_per_day !== undefined) {
    payload.price_per_day = price_per_day;
  }
  if (minimum_stay !== undefined) {
    payload.minimum_stay = minimum_stay;
  }
  if (cancellation !== undefined) {
    payload.cancellation = cancellation;
  }
  if (denom !== undefined) {
    payload.denom = denom;
  }

  const response = await api(route, payload);

  if (response?.success && response.property) {
    dispatchPropertyToStore(dispatch, response.property);
  }

  return response;
};

export const listHostProperty = async (params) =>
  mutateListingStatus({ ...params, shouldList: true });

export const unlistHostProperty = async (params) =>
  mutateListingStatus({ ...params, shouldList: false });

export const updateHostPropertyMetadata = async ({
  tokenId,
  walletAddress,
  metaData,
  short,
  metadataHash,
  dispatch,
}) => {
  if (!tokenId || !walletAddress) {
    throw new Error("tokenId and walletAddress are required");
  }

  const payload = {
    walletAddress: toLowerCaseSafe(walletAddress),
    metaData,
    short,
    metadataHash,
  };

  const response = await axios.patch(
    `${process.env.REACT_APP_BACKEND_SERVER}property/host/properties/${tokenId}/metadata`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
    }
  );

  if (response?.data?.success && response.data.property) {
    dispatchPropertyToStore(dispatch, response.data.property);
  }

  return response?.data;
};

// 🚀 OPTIMIZED: Get my reservations from backend (pre-filtered)
export const getMyReservations = async (account, status = "all", diffToUTC = 0, dispatch) => {
  try {
    const response = await retryOperation(() =>
      api("property/getMyReservations", {
        walletAddress: toLowerCaseSafe(account),
        status: status,
        diffToUTC: diffToUTC,
      })
    );
    
    if (response.success && response.reservations) {
      const nftPayload = response.reservations
        .map((item) => item.nft)
        .filter(Boolean);
      dispatchPropertiesBatch(dispatch, nftPayload);
      
      return response.reservations;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching reservations from backend:", error);
    return [];
  }
};

// 🚀 OPTIMIZED: Get my trips from backend (pre-filtered)
export const getMyTrips = async ({
  account,
  status = "all",
  diffToUTC = 0,
  page = 1,
  limit = 20,
  dispatch,
} = {}) => {
  try {
    if (!account) {
      return {
        trips: [],
        totalCount: 0,
        page,
        limit,
        hasMore: false,
        lastUpdated: null,
      };
    }

    const response = await retryOperation(() =>
      apiGet("property/dashboard/traveler/trips", {
        wallet: toLowerCaseSafe(account),
        status,
        diffToUTC,
        page,
        limit,
      })
    );
    
    const trips = response?.trips || [];
    // 🚀 OPTIMIZED: Only dispatch NFTs to Redux if dispatch is provided and we have trips
    // This prevents unnecessary Redux updates when data is already available via props
    if (trips.length && dispatch) {
      const nftPayload = trips.map((item) => item.nft).filter(Boolean);
      // Only dispatch unique token_ids to avoid duplicates
      const uniquePayload = Array.from(
        new Map(nftPayload.map(item => [item.token_id, item])).values()
      );
      dispatchPropertiesBatch(dispatch, uniquePayload);
    }
    
    return {
      trips,
      totalCount: response?.totalCount ?? trips.length,
      page: response?.page ?? page,
      limit: response?.limit ?? limit,
      hasMore: response?.hasMore ?? false,
      lastUpdated: response?.lastUpdated ?? null,
    };
  } catch (error) {
    console.error("Error fetching trips from backend:", error);
    return {
      trips: [],
      totalCount: 0,
      page,
      limit,
      hasMore: false,
      lastUpdated: null,
    };
  }
};

// 🚀 OPTIMIZED: Get my transactions from backend (pre-filtered, paginated)
export const getMyTransactions = async (account, mode, action = null, page = 1, limit = 100) => {
  try {
    const response = await retryOperation(() =>
      api("property/dashboard/transactions", {
        wallet: toLowerCaseSafe(account),
        mode: mode,
        action: action,
        page: page,
        limit: limit,
      })
    );
    
    if (response.success && response.transactions) {
      return response.transactions;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching transactions from backend:", error);
    return [];
  }
};

export const getTokens = async (account, endpt, dispatch) => {
  try {
    // 🚀 OPTIMIZED: Use backend API only - no blockchain RPC calls
    await getTokensFromBackend(account, dispatch);

    // 🚀 OPTIMIZED: Removed automatic blockchain sync - backend is source of truth
    // Only sync from blockchain if explicitly needed (e.g., after a transaction)
  } catch (error) {
    console.error("Error in getTokens:", error);
  }
};

const updateFromBlockchain = async (properties, endpt, dispatch) => {
  try {
    // Use batch processing instead of sequential updates
    const batchSize = 3; // Process 3 properties at a time to respect rate limits
    
    for (let i = 0; i < properties.length; i += batchSize) {
      const batch = properties.slice(i, i + batchSize);
      
      // Process batch in parallel
      await Promise.all(
        batch.map(property =>
          updateToken(
            {
              token_id: property.token_id,
              contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
            },
            endpt,
            dispatch
          )
        )
      );
      
      // Small delay between batches
      if (i + batchSize < properties.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  } catch (error) {
    console.error("Error updating from blockchain:", error);
  }
};

export const addToken = async (account, token_id, endpt, dispatch) => {
  // 🚀 OPTIMIZED: Use atomic backend API call - no need to reload all tokens
  const res = await api("user/addTokens", {
    account: account,
    token_id: token_id,
  });
  
  // 🚀 OPTIMIZED: Only reload tokens if addToken succeeded and we need to refresh
  // Don't reload all tokens on every addToken call - let socket events handle updates
  if (res) {
    // Optionally refresh only the specific token from backend
    try {
      const property = await api("property/getProperty", { token_id: token_id });
      dispatchPropertyToStore(dispatch, property);
    } catch (error) {
      console.error("Error refreshing token after addToken:", error);
      // Fallback: reload all tokens only if single token refresh fails
      getTokens(account, endpt, dispatch);
    }
  }
};

export const getProfileFromWallet = async (wallet) => {
  const profile = await api("profile/getProfile", {
    walletAddress: wallet,
  });
  return profile;
};

export const getTime = (date) => {
  try {
    let dateObj;
    
    // Handle different input types
    if (!date) {
      dateObj = new Date();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      // Handle timestamp
      dateObj = new Date(date);
    }
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error("Invalid date:", date);
      return new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    }
    
    // Use local time formatting
    return dateObj.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  } catch (error) {
    console.error("Error formatting time:", error);
    return new Date().toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  }
};

export const getDay = (date) => {
  try {
    let dateObj;
    
    // Handle different input types
    if (!date) {
      dateObj = new Date();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      // Handle timestamp
      dateObj = new Date(date);
    }
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error("Invalid date:", date);
      return new Date().toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit' 
      });
    }
    
    const currentTime = new Date();
    
    // Check if it's today
    if (
      currentTime.getFullYear() === dateObj.getFullYear() &&
      currentTime.getMonth() === dateObj.getMonth() &&
      currentTime.getDate() === dateObj.getDate()
    ) {
      return "Today";
    }
    
    // Check if it's yesterday
    const yesterday = new Date(currentTime);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (
      yesterday.getFullYear() === dateObj.getFullYear() &&
      yesterday.getMonth() === dateObj.getMonth() &&
      yesterday.getDate() === dateObj.getDate()
    ) {
      return "Yesterday";
    }
    
    // Return formatted date
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit' 
    });
  } catch (error) {
    console.error("Error formatting day:", error);
    return new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit' 
    });
  }
};

// 🔥 NEW: Helper function to check if a rental reservation is currently active
// Matches backend logic: rental is active if approved, not cancelled, and current time is within rental period
export const isRentalActive = (reservation, diffToUTC = 0) => {
  if (!reservation) return false;
  
  // Must be approved and not cancelled
  if (!reservation.approved || reservation.cancelled) {
    return false;
  }
  
  // Must have valid renting period
  const rentingPeriod = reservation.renting_period;
  if (!Array.isArray(rentingPeriod) || rentingPeriod.length < 2) {
    return false;
  }
  
  const startTime = rentingPeriod[0] - diffToUTC;
  const endTime = rentingPeriod[1] - diffToUTC;
  const now = Math.floor(Date.now() / 1000);
  
  // Validate timestamps
  if (startTime <= 0 || endTime <= 0 || endTime <= startTime) {
    return false;
  }
  
  // Rental is active if current time is within the rental period
  // Backend logic: now >= startTime && now < endTime
  return now >= startTime && now < endTime;
};

// 🔥 NEW: Helper function to check if a rental should be shown in Inbox
// Only shows active rentals or recently completed rentals (within 2 days)
export const shouldShowRentalInInbox = (reservation, diffToUTC = 0) => {
  if (!reservation) return false;
  
  // Must be approved and not cancelled
  if (!reservation.approved || reservation.cancelled) {
    return false;
  }
  
  // Must have valid renting period
  const rentingPeriod = reservation.renting_period;
  if (!Array.isArray(rentingPeriod) || rentingPeriod.length < 2) {
    return false;
  }
  
  const startTime = rentingPeriod[0] - diffToUTC;
  const endTime = rentingPeriod[1] - diffToUTC;
  const now = Math.floor(Date.now() / 1000);
  
  // Validate timestamps
  if (startTime <= 0 || endTime <= 0 || endTime <= startTime) {
    return false;
  }
  
  // Show if rental is currently active
  if (now >= startTime && now < endTime) {
    return true;
  }
  
  // Show if rental was completed within the last 2 days (172800 seconds = 2 days)
  if (now >= endTime) {
    const daysSinceCompletion = (now - endTime) / 86400; // Convert to days
    return daysSinceCompletion <= 2;
  }
  
  // Don't show upcoming or old completed rentals
  return false;
};

// 🔥 FIX: Enhanced getChatId function with better chat identification
export const getChatId = async (sender, receiver, nftId, mode) => {
  try {
    console.log("🔍 Looking for existing chat:", { sender, receiver, nftId, mode });
    
    // Try multiple combinations to find existing chat
    const queries = [
      // Original query
      { renter: sender, owner: receiver, nftId: nftId, mode: mode },
      // Reversed roles in case they were swapped
      { renter: receiver, owner: sender, nftId: nftId, mode: mode },
      // Property-based search (regardless of specific roles)
      { nftId: nftId, mode: mode, $or: [
        { $and: [{ renter: sender }, { owner: receiver }] },
        { $and: [{ renter: receiver }, { owner: sender }] }
      ]}
    ];

    for (const query of queries) {
      const response = await api("chat/getChatId", query);
      if (response) {
        console.log("✅ Found existing chat:", response);
        return response;
      }
    }
    
    console.log("No existing chat found");
    return null;
  } catch (error) {
    console.error("Error getting chat ID:", error);
    return null;
  }
};

// 🔥 NEW: Add function to find or create chat consistently
export const findOrCreateChat = async (sender, receiver, nftId, mode, propertyOwner) => {
  try {
    // First try to find existing chat using the new backend endpoint
    const response = await api("chat/findExistingChat", {
      sender: sender,
      receiver: receiver,
      nftId: nftId,
      mode: mode
    });
    
    if (response && response.chatId) {
      return { chatId: response.chatId, isNew: false };
    }
    
    // If no existing chat, we'll let the sendMessage create it
    return { chatId: null, isNew: true };
  } catch (error) {
    console.error("Error in findOrCreateChat:", error);
    return { chatId: null, isNew: true };
  }
};

export const getIdFromHash = async (hash) => {
  return await api("tx/fetchId", {
    hash: hash,
  });
};

const BATCH_SIZE = 3; // Reduced from 10 to 3 for rate limiting
const BATCH_DELAY = 1000; // Reduced from 2000 to 1000ms

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const syncAllNFTs = async (endpt, dispatch) => {
  try {
    const message = {
      all_tokens: {},
    };

    // Use rate-limited query for getting all token IDs
    const allTokens = await nibiruApi.queryContractWithLimit(
      process.env.REACT_APP_RENTAL_SMART_CONTRACT,
      message,
      'high'
    );

    console.log("Found tokens:", allTokens?.token_ids?.length || 0);

    if (allTokens?.token_ids) {
      for (let i = 0; i < allTokens.token_ids.length; i += BATCH_SIZE) {
        const batch = allTokens.token_ids.slice(i, i + BATCH_SIZE);

        const updatePromises = batch.map((token_id) =>
          updateToken(
            {
              token_id: token_id,
              contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
            },
            endpt,
            dispatch,
            true
          ).catch((error) => {
            console.error(`Failed to update token ${token_id}:`, error);
            return null;
          })
        );

        await Promise.all(updatePromises);
        console.log(
          `Processed batch ${i / BATCH_SIZE + 1}/${Math.ceil(
            allTokens.token_ids.length / BATCH_SIZE
          )}`
        );

        if (i + BATCH_SIZE < allTokens.token_ids.length) {
          await sleep(BATCH_DELAY);
        }
      }
      console.log("All tokens synced successfully");
    }

    return true;
  } catch (error) {
    console.error("Error syncing NFTs:", error);
    return false;
  }
};

// 🔥 FIX: Enhanced updateToken with better error handling
export const updateTokenSafe = async (tokenKey, endpointTm, dispatch) => {
  try {
    // Validate token_id
    if (!tokenKey?.token_id || 
        tokenKey.token_id === 'undefined' || 
        tokenKey.token_id === 'null' ||
        tokenKey.token_id === null ||
        tokenKey.token_id === undefined) {
      console.error("❌ Invalid token_id provided:", tokenKey?.token_id);
      return false;
    }

    console.log("🔄 updateTokenSafe: Fetching token:", tokenKey.token_id);

    // Ensure token_id is string and clean
    const cleanTokenId = tokenKey.token_id.toString().trim();
    
    if (!cleanTokenId) {
      console.error("❌ Empty token_id after cleaning:", tokenKey.token_id);
      return false;
    }

    // Call the original updateToken function with clean data
    // This will now use rate-limited queries internally
    await updateToken({
      ...tokenKey,
      token_id: cleanTokenId
    }, endpointTm, dispatch);
    
    return true;
  } catch (error) {
    console.error("💥 updateTokenSafe: Error:", tokenKey?.token_id, error);
    return false;
  }
};